import { pendingBattleId } from '../../lib/economyClient';
import { economyFetch as fetch } from '../../lib/economyClient';
/**
 * アドベンチャーモード サーバー連携
 *
 * Edge Function（adventure）と連携し、
 * 進捗データの取得・同期・対戦記録を行う
 */

import type { AdventureProgress, NpcId, BattleResult, PackReward } from '../data/types';

// ========================================
// Supabase設定
// ========================================

const SUPABASE_URL = 'https://vikqkqwklbdjfhkydzpw.supabase.co';
const AUTH_STORAGE_KEY = 'sb-vikqkqwklbdjfhkydzpw-auth-token';
const API_TIMEOUT_MS = 10000;

function getAccessToken(): string | null {
  try {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed?.access_token || null;
    }
  } catch (err) {
    console.error('[useAdventureServer] Failed to get access token:', err);
  }
  return null;
}

function withTimeout<T>(promise: Promise<T>, ms: number, operation: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new Error(`${operation}: タイムアウト（${ms}ms）`));
    }, ms);

    promise
      .then((result) => {
        clearTimeout(timeout);
        resolve(result);
      })
      .catch((error) => {
        clearTimeout(timeout);
        reject(error);
      });
  });
}

// ========================================
// レスポンス型
// ========================================

interface ApiResponse<T = unknown> {
  success: boolean;
  error?: string;
  progress?: AdventureProgress;
  merged_progress?: AdventureProgress;
  conflict_resolution?: 'server_newer' | 'local_newer' | 'merged';
  today_reward_count?: number;
  new_gold_balance?: number;
  new_unopened_packs?: number;
}

// ========================================
// サーバー可用性チェック
// ========================================

/**
 * サーバーが利用可能かどうかを確認
 * （認証トークンが存在するか）
 */
export function isAdventureServerAvailable(): boolean {
  return getAccessToken() !== null;
}

// ========================================
// API関数
// ========================================

/**
 * サーバーから進捗を取得
 */
export async function fetchAdventureProgress(): Promise<AdventureProgress | null> {
  const token = getAccessToken();
  if (!token) {
    console.log('[useAdventureServer] No auth token, skipping server fetch');
    return null;
  }

  try {
    const response = await withTimeout(
      fetch(`${SUPABASE_URL}/functions/v1/adventure`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }),
      API_TIMEOUT_MS,
      'fetchAdventureProgress'
    );

    if (!response.ok) {
      console.error('[useAdventureServer] Server returned error:', response.status);
      return null;
    }

    const data: ApiResponse = await response.json();
    if (data.success && data.progress) {
      console.log('[useAdventureServer] Fetched progress from server');
      return data.progress;
    }

    return null;
  } catch (error) {
    console.error('[useAdventureServer] Failed to fetch progress:', error);
    return null;
  }
}

/**
 * ローカルデータをサーバーに同期
 */
export async function syncAdventureProgress(
  localProgress: AdventureProgress,
  force = false
): Promise<{
  progress: AdventureProgress | null;
  resolution: 'server_newer' | 'local_newer' | 'merged' | null;
}> {
  const token = getAccessToken();
  if (!token) {
    console.log('[useAdventureServer] No auth token, skipping sync');
    return { progress: null, resolution: null };
  }

  try {
    const response = await withTimeout(
      fetch(`${SUPABASE_URL}/functions/v1/adventure/sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          local_progress: localProgress,
          force,
        }),
      }),
      API_TIMEOUT_MS,
      'syncAdventureProgress'
    );

    if (!response.ok) {
      console.error('[useAdventureServer] Sync failed:', response.status);
      return { progress: null, resolution: null };
    }

    const data: ApiResponse = await response.json();
    if (data.success && data.merged_progress) {
      console.log('[useAdventureServer] Sync completed:', data.conflict_resolution);
      return {
        progress: data.merged_progress,
        resolution: data.conflict_resolution || null,
      };
    }

    return { progress: null, resolution: null };
  } catch (error) {
    console.error('[useAdventureServer] Failed to sync:', error);
    return { progress: null, resolution: null };
  }
}

/**
 * 対戦結果をサーバーに記録（報酬も同時に付与）
 */
export async function recordBattleToServer(
  npcId: NpcId,
  result: BattleResult,
  isFirstClear: boolean,
  goldReward: number = 0,
  packRewards: PackReward[] = []
): Promise<{
  progress: AdventureProgress | null;
  newGoldBalance: number | null;
  newUnopenedPacks: number | null;
}> {
  const token = getAccessToken();
  if (!token) {
    console.log('[useAdventureServer] No auth token, skipping battle record');
    return { progress: null, newGoldBalance: null, newUnopenedPacks: null };
  }

  try {
    const response = await withTimeout(
      fetch(`${SUPABASE_URL}/functions/v1/adventure/battle`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          npc_id: npcId,
          battle_id: pendingBattleId('adventure'),
          result,
          is_first_clear: isFirstClear,
          gold_reward: goldReward,
          pack_rewards: packRewards,
        }),
      }),
      API_TIMEOUT_MS,
      'recordBattleToServer'
    );

    if (!response.ok) {
      console.error('[useAdventureServer] Record battle failed:', response.status);
      return { progress: null, newGoldBalance: null, newUnopenedPacks: null };
    }

    const data: ApiResponse = await response.json();
    if (data.success && data.progress) {
      console.log('[useAdventureServer] Battle recorded to server', {
        newGoldBalance: data.new_gold_balance,
        newUnopenedPacks: data.new_unopened_packs,
      });
      return {
        progress: data.progress,
        newGoldBalance: data.new_gold_balance ?? null,
        newUnopenedPacks: data.new_unopened_packs ?? null,
      };
    }

    return { progress: null, newGoldBalance: null, newUnopenedPacks: null };
  } catch (error) {
    console.error('[useAdventureServer] Failed to record battle:', error);
    return { progress: null, newGoldBalance: null, newUnopenedPacks: null };
  }
}

/**
 * 報酬獲得をサーバーに記録
 */
export async function recordRewardToServer(npcId: NpcId): Promise<{
  progress: AdventureProgress | null;
  todayRewardCount: number | null;
}> {
  const token = getAccessToken();
  if (!token) {
    return { progress: null, todayRewardCount: null };
  }

  try {
    const response = await withTimeout(
      fetch(`${SUPABASE_URL}/functions/v1/adventure/reward`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ npc_id: npcId }),
      }),
      API_TIMEOUT_MS,
      'recordRewardToServer'
    );

    if (!response.ok) {
      return { progress: null, todayRewardCount: null };
    }

    const data: ApiResponse = await response.json();
    if (data.success) {
      return {
        progress: data.progress || null,
        todayRewardCount: data.today_reward_count ?? null,
      };
    }

    return { progress: null, todayRewardCount: null };
  } catch (error) {
    console.error('[useAdventureServer] Failed to record reward:', error);
    return { progress: null, todayRewardCount: null };
  }
}

/**
 * 称号をサーバーに追加
 */
export async function addTitleToServer(title: string): Promise<AdventureProgress | null> {
  const token = getAccessToken();
  if (!token) {
    return null;
  }

  try {
    const response = await withTimeout(
      fetch(`${SUPABASE_URL}/functions/v1/adventure/title`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ title }),
      }),
      API_TIMEOUT_MS,
      'addTitleToServer'
    );

    if (!response.ok) {
      return null;
    }

    const data: ApiResponse = await response.json();
    return data.success ? (data.progress || null) : null;
  } catch (error) {
    console.error('[useAdventureServer] Failed to add title:', error);
    return null;
  }
}
